import store from '@state/store'
import authLayout from '@router/layouts/auth.vue';
import mainLayout from '@router/layouts/main.vue';
import roomLayout from '@router/layouts/room.vue';

const routeList = [
  {
    path: '/',
    name: 'index',
    components: {
      layout: mainLayout
    },
    meta: {
      authRequired: true,
    },
    redirect:{ name: 'home' },
    children: [
      {
        path: "/",
        name: "home",
        meta: {
          title: 'Home',
        },
        component: () => lazyLoadView(import('@views/home/index.vue')),
      },
      {
        path: "table-management",
        name: "table-management",
        meta: {
          title: 'Table Management',
        },
        component: () => lazyLoadView(import('@views/table-management/index.vue')),
      },
      {
        path: "guest-list",
        name: "guest-list",
        meta: {
          title: 'Guest List',
        },
        component: () => lazyLoadView(import('@views/guest-list/index.vue')),
      },
    ]
  },
  {
    path: '/auth',
    name: 'auth',
    components: {
      layout: authLayout
    },
    redirect: { name: 'login' },
    children: [
      {
        path: 'login',
        name: 'login',
        component: () => lazyLoadView(import('@views/auth/login.vue')),
        meta: {
          beforeResolve(routeTo, routeFrom, next) {
            // If the user is already logged in
            if (store.getters['auth/loggedIn']) {
              // Redirect to the home page instead
              next({ name: 'home' })
            } else {
              // Continue to the login page
              next()
            }
          },
        },
      },
      {
        path: '/logout',
        name: 'logout',
        meta: {
          authRequired: true,
          beforeResolve(routeTo, routeFrom, next) {
            store.dispatch('auth/logOut')
            const authRequiredOnPreviousRoute = routeFrom.matched.some(
              (route) => route.meta.authRequired
            )
            // Navigate back to previous page, or home as a fallback
            next(authRequiredOnPreviousRoute ? { name: 'login' } : { ...routeFrom })
          },
        },
      },
    ]
  },
  {
    path: '/',
    name: 'checkin-info',
    components: {
      layout: authLayout
    },
    redirect:{ name: 'self-checkin-info' },
    children: [
      {
        path: "self-checkin-info",
        name: "self-checkin-info",
        meta: {
          title: 'Self Checkin Info',
        },
        component: () => lazyLoadView(import('@views/checkin/checkin-info.vue')),
      },
    ],
  },
  {
    path: '/room',
    components: {
      layout: roomLayout
    },
    redirect: { name: 'room-home-page' },
    children: [
      {
        path: 'room-home-page',
        name: 'room-home-page',
        meta: {
          title: 'Room Home Page',
        },
        component: () => lazyLoadView(import('@views/room/index.vue')),
      },
      {
        path: 'multimedia',
        name: 'multimedia',
        meta: {
          title: 'Multimedia',
        },
        component: () => lazyLoadView(import('@views/multimedia/index.vue')),
      },
      {
        path: 'room-guest-list',
        name: 'room-guest-list',
        meta: {
          title: 'Guest Room List',
        },
        component: () => lazyLoadView(import('@views/guest-room-list/index.vue')),
      },
    ],
  },
  {
    path: '/',
    name: 'do-checkin',
    components: {
      layout: mainLayout
    },
    redirect:{ name: 'checkin' },
    children: [
      {
        path: "/e/:visibleEventCode/checkin",
        name: "checkin",
        meta: {
          title: 'Checkin',
        },
        component: () => lazyLoadView(import('@views/checkin/checkin.vue')),
      },
    ],
  },
  {
    path: '/',
    name: 'utility',
    components: {
      layout: authLayout
    },
    redirect:{ name: '404' },
    children: [
      {
        path: '/404',
        name: '404',
        component: require('@views/system/_404.vue').default,
        // Allows props to be passed to the 404 page through route
        // params, such as `resource` to define what wasn't found.
        props: true,
      },
    ],
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    redirect: '404',
  },
]

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: require('@views/system/_loading.vue').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    error: require('@views/system/_timeout.vue').default,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children)
    },
  })
}

export default routeList
